import { useState, useEffect, useCallback } from 'react';
import { ExternalLink, RotateCcw, Trash2, Archive, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase, deleteFile } from '../../lib/supabase';
import { useOwner } from '../../hooks/useOwner';

// "Previous versions" list for one document slot.
//   vehicleId / slot  – which archived files to show (see lib/fileArchive.js)
//   current           – { url, path } of the file currently attached (it is archived when another is restored)
//   target            – { table, rowId, urlField, pathField? } where the current file is stored, so Restore can swap it
//   onChanged         – called after a restore so the parent can reload
// Restore needs edit rights; permanent delete needs an owner or admin.
export const ArchivedFiles = ({ vehicleId, slot, current, target, onChanged }) => {
  const { isOwner, isAdmin, role } = useOwner();
  const canRestore = isOwner || isAdmin || role === 'editor';
  const canDelete = isOwner || isAdmin;

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!vehicleId || !slot) return;
    const { data, error } = await supabase
      .from('file_archive')
      .select('*')
      .eq('vehicle_id', vehicleId)
      .eq('slot', slot)
      .order('archived_at', { ascending: false });
    if (!error) setItems(data || []);
  }, [vehicleId, slot]);

  useEffect(() => { load(); }, [load, current?.url]);

  const restore = async (item) => {
    if (!target) return;
    if (!window.confirm('Make this the current document? The current one moves to previous versions.')) return;
    setBusy(item.id);
    try {
      const patch = { [target.urlField]: item.url };
      if (target.pathField) patch[target.pathField] = item.file_path || null;
      const { data, error } = await supabase.from(target.table).update(patch).eq('id', target.rowId).select('id');
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Not allowed to change this record');

      // Swap: the file that was current takes the restored file's place in the archive,
      // so nothing is lost and editors (who cannot delete) can restore too.
      if (current?.url) {
        await supabase.from('file_archive')
          .update({ url: current.url, file_path: current.path || null, archived_at: new Date().toISOString() })
          .eq('id', item.id);
      } else {
        await supabase.from('file_archive').delete().eq('id', item.id);
      }
      toast.success('Previous version restored');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error('Could not restore: ' + err.message);
    }
    setBusy(null);
  };

  const remove = async (item) => {
    if (!window.confirm('Permanently delete this archived file? This cannot be undone.')) return;
    setBusy(item.id);
    try {
      const { error } = await supabase.from('file_archive').delete().eq('id', item.id);
      if (error) throw error;
      if (item.file_path) { try { await deleteFile(item.file_path); } catch (e) { /* file may already be gone */ } }
      toast.success('Archived file deleted');
      await load();
    } catch (err) {
      toast.error('Could not delete: ' + err.message);
    }
    setBusy(null);
  };

  if (items.length === 0) return null;

  return (
    <div style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface2)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 13, fontWeight: 600 }}
      >
        <Archive size={14} />
        Previous versions ({items.length})
        <span style={{ marginLeft: 'auto', display: 'flex' }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderTop: '1px solid var(--border)' }}>
          {items.map(it => (
            <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  Replaced {new Date(it.archived_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
                {it.label && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{it.label}</div>}
              </div>
              <a href={it.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                <ExternalLink size={12} /> View
              </a>
              {canRestore && target && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy === it.id} onClick={() => restore(it)}>
                  <RotateCcw size={12} /> Restore
                </button>
              )}
              {canDelete && (
                <button type="button" className="btn btn-danger btn-sm" disabled={busy === it.id} onClick={() => remove(it)} title="Delete permanently">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
